import type { ReactElement } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { INVENTORY_VISIBILITY_OPTIONS } from '@kirin/api-client/contracts/settings';

/**
 * Sentinel select value for the "Never" option, since a Radix Select item
 * value must be a non-empty string (null cannot be represented directly).
 */
const NEVER_VALUE = 'never';

function toSelectValue(hideAfterDays: number | null): string {
  return hideAfterDays === null ? NEVER_VALUE : String(hideAfterDays);
}

function fromSelectValue(value: string): number | null {
  return value === NEVER_VALUE ? null : Number(value);
}

interface InventoryVisibilityControlProps {
  /** Configured window in days, or `null` for "Never". */
  value: number | null;
  onChange: (hideAfterDays: number | null) => void;
  saving?: boolean;
  canEdit?: boolean;
}

/**
 * Dropdown for the organization "hide stale inventory entries after" setting
 * (KIR-3027). Options are `7 / 14 / 30 / 60 / 90 days / Never`.
 */
export function InventoryVisibilityControl({
  value,
  onChange,
  saving = false,
  canEdit = true,
}: InventoryVisibilityControlProps): ReactElement {
  return (
    <Select
      value={toSelectValue(value)}
      onValueChange={v => onChange(fromSelectValue(v))}
      disabled={saving || !canEdit}
    >
      <SelectTrigger className="w-[180px]" aria-label="Hide entries after">
        <SelectValue placeholder="Select period" />
      </SelectTrigger>
      <SelectContent>
        {INVENTORY_VISIBILITY_OPTIONS.map(option => (
          <SelectItem
            key={toSelectValue(option.value)}
            value={toSelectValue(option.value)}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

InventoryVisibilityControl.displayName = 'InventoryVisibilityControl';
